import React from 'react';
import { Process } from '../../types';
import type { Segment } from '../../migrations/legacyEdge';
import { anchorPoint } from '../../utils/geometry';
import type { SegmentSemanticStyle } from '../../utils/mapping/semanticStyles';

interface ThreadProps {
  seg:        Segment;
  segIdx:     number;
  pairIdx:    number;
  pairTotal:  number;
  proc:       Process;
  procIndex:  number;
  totalProcs: number;
  fromX:      number;
  fromY:      number;
  toX:        number;
  toY:        number;
  onClick:    () => void;
  onEndpointMouseDown: (
    e: React.MouseEvent<SVGCircleElement>,
    endpoint: 'from' | 'to',
    point: { x: number; y: number }
  ) => void;
  isEndpointDragActive?: boolean;
  isHovered?: boolean;
  onHoverChange?: (hovered: boolean) => void;
  semanticStyle?: SegmentSemanticStyle;
  isPlaybackVisible?: boolean;
}

const Thread: React.FC<ThreadProps> = ({
  seg, segIdx, pairIdx, pairTotal, proc, procIndex, totalProcs,
  fromX, fromY, toX, toY, onClick, onEndpointMouseDown,
  isEndpointDragActive = false, isHovered = false, onHoverChange, semanticStyle, isPlaybackVisible = true,
}) => {
  const [ax1, ay1] = anchorPoint(fromX, fromY, procIndex, totalProcs);
  const [ax2, ay2] = anchorPoint(toX,   toY,   procIndex, totalProcs);

  const bs        = procIndex % 2 === 0 ? 1 : -1;
  const bw        = 0.10 + procIndex * 0.03;
  const flowType  = seg.flowType ?? 'normal';
  const isParallel = semanticStyle?.forceParallel ?? (flowType === 'parallel');
  const isWait     = flowType === 'wait';
  const bowFactor = flowType === 'loop' ? 0.6 : bw;
  const baseTangentX = ax2 - ax1;
  const baseTangentY = ay2 - ay1;
  const baseLen = Math.hypot(baseTangentX, baseTangentY) || 1;
  const baseNormalX = -baseTangentY / baseLen;
  const baseNormalY = baseTangentX / baseLen;
  const pairCenteredIndex = pairIdx - (pairTotal - 1) / 2;
  const pairCurveOffset = pairCenteredIndex * 8;

  const x1 = ax1 + baseNormalX * pairCurveOffset;
  const y1 = ay1 + baseNormalY * pairCurveOffset;
  const x2 = ax2 + baseNormalX * pairCurveOffset;
  const y2 = ay2 + baseNormalY * pairCurveOffset;

  const cmx       = (x1 + x2) / 2 + (y2 - y1) * bowFactor * bs + baseNormalX * pairCurveOffset * 0.45;
  const cmy       = (y1 + y2) / 2 + (x1 - x2) * bowFactor * bs + baseNormalY * pairCurveOffset * 0.45;
  const d         = `M${x1},${y1} Q${cmx},${cmy} ${x2},${y2}`;
  const midX      = 0.25 * x1 + 0.5 * cmx + 0.25 * x2;
  const midY      = 0.25 * y1 + 0.5 * cmy + 0.25 * y2;
  const tangentX   = x2 - x1;
  const tangentY   = y2 - y1;
  const tangentLen = Math.hypot(tangentX, tangentY) || 1;
  const tangentUnitX = tangentX / tangentLen;
  const tangentUnitY = tangentY / tangentLen;
  const normalX    = -tangentUnitY;
  const normalY    = tangentUnitX;
  const procCenteredIndex = procIndex - (totalProcs - 1) / 2;
  const normalOffset = procCenteredIndex * 4;
  const tangentOffset = ((segIdx % 3) - 1) * 10;
  const labelX    = midX + normalX * normalOffset + tangentUnitX * tangentOffset;
  const labelBaseY = flowType === 'loop' ? midY - 18 : midY;
  const labelY    = labelBaseY + normalY * normalOffset + tangentUnitY * tangentOffset;
  const parallelOffset = 4;
  const dParallelUp = `M${x1},${y1 - parallelOffset} Q${cmx},${cmy - parallelOffset} ${x2},${y2 - parallelOffset}`;
  const dParallelDown = `M${x1},${y1 + parallelOffset} Q${cmx},${cmy + parallelOffset} ${x2},${y2 + parallelOffset}`;

  const friction = seg.friction;
  let frictionColor: string | null = null;
  if (friction === 'low') frictionColor = '#d6b256';
  else if (friction === 'medium') frictionColor = '#e0a800';
  else if (friction === 'high') frictionColor = '#c0392b';

  const isNonBlocking = seg.blocking === 'non-blocking';
  const dashMap: Record<string, string | undefined> = {
    normal: isNonBlocking ? '8 5'     : undefined,
    branch: isNonBlocking ? '6 4 2 4' : '8 4',
    loop:   isNonBlocking ? '2 4'     : '2 5',
    parallel: isNonBlocking ? '10 4 2 4' : '10 4',
    wait:   isNonBlocking ? '12 6'    : '12 6',
    fail:   undefined,
  };
  const sw      = semanticStyle?.strokeWidth ?? (flowType === 'fail' ? 1.8 : 2.5);
  const lineOpacity = semanticStyle?.lineOpacity;
  const badgeT = 0.14;
  const oneMinusT = 1 - badgeT;
  const badgeCurveX = oneMinusT * oneMinusT * x1 + 2 * oneMinusT * badgeT * cmx + badgeT * badgeT * x2;
  const badgeCurveY = oneMinusT * oneMinusT * y1 + 2 * oneMinusT * badgeT * cmy + badgeT * badgeT * y2;
  const orderBadgeX = badgeCurveX + normalX * 6;
  const orderBadgeY = badgeCurveY + normalY * 6;
  const markerId = `thread-arrow-${proc.id}-${procIndex}-${segIdx}`;
  const markerEnd = `url(#${markerId})`;
  const fromToLen = Math.hypot(x2 - x1, y2 - y1) || 1;
  const fromDirX = (x2 - x1) / fromToLen;
  const fromDirY = (y2 - y1) / fromToLen;
  const toDirX = (x1 - x2) / fromToLen;
  const toDirY = (y1 - y2) / fromToLen;
  const handleOffset = 16;
  const fromHandleX = x1 + fromDirX * handleOffset;
  const fromHandleY = y1 + fromDirY * handleOffset;
  const toHandleX = x2 + toDirX * handleOffset;
  const toHandleY = y2 + toDirY * handleOffset;
  const showHandles = isHovered || isEndpointDragActive;
  const hoverGlowOpacity = isHovered ? 0.32 : 0;
  const hoverGlowWidth = (sw ?? 2.5) + 7;
  const playbackOpacity = isPlaybackVisible ? 1 : 0.12;

  return (
    <g
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
      style={{ opacity: playbackOpacity, transition: 'opacity 0.25s ease' }}
    >
      <defs>
        <marker
          id={markerId}
          markerWidth="8"
          markerHeight="8"
          refX="7"
          refY="4"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L8,4 L0,8 Z" fill={proc.color} opacity={0.95} />
        </marker>
      </defs>

      {/* Halo de fricción */}
      {frictionColor && (
        <path d={d} fill="none" stroke={frictionColor}
          strokeWidth={8} strokeLinecap="round" opacity={0.22} pointerEvents="none" />
      )}

      {/* Hilo principal */}
      {isParallel ? (
        <g cursor="pointer" className="thread-line">
          <path
            d={dParallelUp}
            fill="none"
            stroke={proc.color}
            strokeWidth={hoverGlowWidth}
            strokeLinecap="round"
            opacity={hoverGlowOpacity}
            pointerEvents="none"
          />
          <path
            d={dParallelDown}
            fill="none"
            stroke={proc.color}
            strokeWidth={hoverGlowWidth}
            strokeLinecap="round"
            opacity={hoverGlowOpacity}
            pointerEvents="none"
          />
          <path
            d={dParallelUp}
            fill="none"
            stroke="transparent"
            strokeWidth={14}
            strokeLinecap="round"
            onClick={e => { e.stopPropagation(); onClick(); }}
          />
          <path
            d={dParallelDown}
            fill="none"
            stroke="transparent"
            strokeWidth={14}
            strokeLinecap="round"
            onClick={e => { e.stopPropagation(); onClick(); }}
          />
          <path
            d={dParallelUp} stroke={proc.color} fill="none"
            strokeWidth={sw} strokeLinecap="round" opacity={lineOpacity ?? (isHovered ? 1 : 0.85)}
            strokeDasharray={semanticStyle?.dashArray ?? dashMap[flowType]}
            markerEnd={markerEnd}
          />
          <path
            d={dParallelDown} stroke={proc.color} fill="none"
            strokeWidth={sw} strokeLinecap="round" opacity={lineOpacity ?? (isHovered ? 1 : 0.85)}
            strokeDasharray={semanticStyle?.dashArray ?? dashMap[flowType]}
            markerEnd={markerEnd}
          />
        </g>
      ) : (
        <g cursor="pointer" className="thread-line">
          <path
            d={d}
            fill="none"
            stroke={proc.color}
            strokeWidth={hoverGlowWidth}
            strokeLinecap="round"
            opacity={hoverGlowOpacity}
            pointerEvents="none"
          />
          <path
            d={d}
            fill="none"
            stroke="transparent"
            strokeWidth={14}
            strokeLinecap="round"
            onClick={e => { e.stopPropagation(); onClick(); }}
          />
          <path
            d={d} stroke={proc.color} fill="none"
            strokeWidth={sw} strokeLinecap="round" opacity={lineOpacity ?? (isHovered ? 1 : 0.85)}
            strokeDasharray={semanticStyle?.dashArray ?? dashMap[flowType]}
            markerEnd={markerEnd}
            onClick={e => { e.stopPropagation(); onClick(); }}
          />
        </g>
      )}

      {/* Decoración de bucle */}
      {flowType === 'loop' && (
        <g pointerEvents="none">
          <circle cx={midX} cy={midY} r={10} fill="none"
            stroke={proc.color} strokeWidth={1.5} opacity={0.55} strokeDasharray="3 3" />
          <path
            d={`M${midX + 10},${midY} L${midX + 5},${midY - 4} M${midX + 10},${midY} L${midX + 5},${midY + 4}`}
            stroke={proc.color} strokeWidth={1.5} strokeLinecap="round" fill="none" opacity={0.75}
          />
        </g>
      )}

      {/* Decoración de espera */}
      {isWait && (
        <g pointerEvents="none">
          <path
            d={`M${midX},${midY - 7} L${midX + 7},${midY} L${midX},${midY + 7} L${midX - 7},${midY} Z`}
            fill="rgba(255,255,255,0.92)"
            stroke={proc.color}
            strokeWidth={1.4}
            opacity={0.9}
          />
        </g>
      )}

      {/* Etiqueta de orden */}
      <g pointerEvents="none">
        <circle cx={orderBadgeX} cy={orderBadgeY} r={7} fill="rgba(255,255,255,0.92)" stroke={proc.color} strokeWidth={1.1} />
        <text
          x={orderBadgeX}
          y={orderBadgeY + 0.3}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={8}
          fill={proc.color}
          fontWeight={700}
          fontFamily="'DM Mono',monospace"
        >
          {segIdx + 1}
        </text>
      </g>

      {/* Etiqueta */}
      {seg.label  && (
        <g pointerEvents="none">
          <rect x={labelX - Math.min(seg.label.length * 7 + 12, 140) / 2} y={labelY - 9}
            width={Math.min(seg.label.length * 7 + 12, 140)} height={15} rx={4}
            fill="rgba(245,242,236,0.92)" />
          <text x={labelX} y={labelY} textAnchor="middle" dominantBaseline="central"
            fontSize={11} fill={proc.color} fontFamily="'Caveat',cursive">
            {seg.label }
          </text>
        </g>
      )}


    
      {/* Condición */}
      {seg.condition && (
        <g pointerEvents="none">
          <rect x={labelX - Math.min(seg.condition.length * 5.5 + 12, 160) / 2}
            y={labelY + (seg.label ? 12 : 0) + 2}
            width={Math.min(seg.condition.length * 5.5 + 12, 160)} height={12} rx={4}
            fill="rgba(238,242,250,0.92)" />
          <text x={labelX} y={labelY + (seg.label ? 12 : 0) + 9}
            textAnchor="middle" dominantBaseline="central"
            fontSize={9} fill="#4a7fb5" fontFamily="'DM Mono',monospace">
            {seg.condition}
          </text>
        </g>
      )}

      {/* Dots de extremos / handles drag&drop */}
      <g style={{ opacity: showHandles ? 1 : 0, transition: 'opacity 0.12s ease' }}>
        <circle
          cx={fromHandleX}
          cy={fromHandleY}
          r={9.5}
          fill={isEndpointDragActive ? `${proc.color}33` : 'rgba(255,255,255,0.9)'}
          stroke={proc.color}
          strokeWidth={1.5}
          style={{ cursor: 'grab' }}
          onMouseDown={e => onEndpointMouseDown(e, 'from', { x: x1, y: y1 })}
        />
        <text
          x={fromHandleX}
          y={fromHandleY}
          textAnchor="middle"
          dominantBaseline="central"
          fill={proc.color}
          fontSize={8}
          fontFamily="'DM Mono',monospace"
          pointerEvents="none"
        >
          O
        </text>
        <circle cx={x1} cy={y1} r={3.5} fill={proc.color} />

        <circle
          cx={toHandleX}
          cy={toHandleY}
          r={10}
          fill={isEndpointDragActive ? `${proc.color}33` : 'rgba(255,255,255,0.9)'}
          stroke={proc.color}
          strokeWidth={1.5}
          style={{ cursor: 'grab' }}
          onMouseDown={e => onEndpointMouseDown(e, 'to', { x: x2, y: y2 })}
        />
        <text
          x={toHandleX}
          y={toHandleY}
          textAnchor="middle"
          dominantBaseline="central"
          fill={proc.color}
          fontSize={8}
          fontFamily="'DM Mono',monospace"
          pointerEvents="none"
        >
          D
        </text>
        <circle cx={x2} cy={y2} r={5} fill={proc.color} opacity={0.9} />
        {isEndpointDragActive && (
          <>
            <circle cx={x1} cy={y1} r={7} fill="none" stroke={proc.color} strokeWidth={1.5} opacity={0.5} />
            <circle cx={x2} cy={y2} r={8} fill="none" stroke={proc.color} strokeWidth={1.5} opacity={0.5} />
          </>
        )}
      </g>
    </g>
  );
};

export default Thread;
